// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import isApiError from "@insite/client-framework/Common/isApiError";
import { sendToShell } from "@insite/client-framework/Components/ShellHole";
import { fetch } from "@insite/client-framework/ServerSideRendering";
import { patch, ServiceResult } from "@insite/client-framework/Services/ApiService";
import {
    ResetPasswordApiParameter,
    SendAccountActivationEmailApiParameter,
    Session,
    UpdateSessionApiParameter,
} from "@insite/client-framework/Services/SessionService";
import translate from "@insite/client-framework/Translate";
import { BillToModel, SessionModel, ShipToModel } from "@insite/client-framework/Types/ApiModels";

const sessionsUrl = "/api/v1/sessions";

export async function updateSession(parameter: UpdateSessionApiParameter) {
    const patchedSession = {
        ...parameter.session,
    };

    if (patchedSession.billToId) {
        (patchedSession as SessionModel).billTo = { id: patchedSession.billToId } as BillToModel;
        delete patchedSession.billToId;
    }

    if (patchedSession.shipToId) {
        (patchedSession as SessionModel).shipTo = { id: patchedSession.shipToId } as ShipToModel;
        delete patchedSession.shipToId;
    }

    const response = await fetch(`${sessionsUrl}/current`, {
        method: "PATCH",
        headers: {
            authorization: `Bearer ${parameter.accessToken}`,
            Accept: "application/json, text/plain, */*",
            "Content-Type": "application/json",
        },
        body: JSON.stringify(patchedSession),
    });

    const session = ((await response.json()) as Promise<SessionModel>) as any;

    if (response.status >= 400 && response.status < 600) {
        throw new Error((session.message as any) || translate("An error occurred, please try again later."));
    }

    cleanSession(session);
    informShell(session);

    return session;
}

function cleanSession(session: SessionModel) {
    if (session.billTo) {
        (session as Session).billToId = session.billTo.id;
        delete (session as Partial<SessionModel>).billTo;
    }

    if (session.shipTo) {
        (session as Session).shipToId = session.shipTo.id;
        delete (session as Partial<SessionModel>).shipTo;
    }
}

function informShell(session: SessionModel) {
    sendToShell({
        type: "FrontEndSessionLoaded",
        personas: session.personas,
    });
}

export async function sendAccountActivationEmail(
    parameter: SendAccountActivationEmailApiParameter,
): Promise<ServiceResult<SessionModel>> {
    try {
        (parameter as any)!.activateAccount = !!(parameter as any)?.activateAccount;

        const session = await patch<SessionModel>(`${sessionsUrl}/current`, {
            ...parameter,
        });
        cleanSession(session);
        return {
            successful: true,
            result: session,
        };
    } catch (error) {
        if (isApiError(error) && error.status === 400) {
            return {
                successful: false,
                errorMessage: error.errorJson.message,
            };
        }
        throw error;
    }
}

export async function resetPassword(parameter: ResetPasswordApiParameter): Promise<ServiceResult<Session>> {
    try {
        const session = await patch<SessionModel>("/api/v1/sessions/current", {
            ...parameter,
            resetPassword: false,
        });
        cleanSession(session);
        return {
            successful: true,
            result: session,
        };
    } catch (error) {
        if ("status" in error && error.status === 400 && error.errorJson && error.errorJson.message) {
            return {
                successful: false,
                errorMessage: error.errorJson.message,
            };
        }
        throw error;
    }
}